import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contractorApi } from '../../lib/contractorApi';

interface Appointment {
  id: string;
  appointment_date: string;
  appointment_time: string;
  duration_hours: number;
  notes: string | null;
  status: 'proposed' | 'confirmed' | 'cancelled' | 'completed';
  proposed_at: string;
}

function statusBadge(status: string) {
  const cfg: Record<string, { bg: string; text: string; label: string }> = {
    proposed:  { bg: '#eff6ff', text: '#1d4ed8', label: 'Proposed' },
    confirmed: { bg: '#f0fdf4', text: '#16a34a', label: 'Confirmed' },
    cancelled: { bg: '#fef2f2', text: '#dc2626', label: 'Cancelled' },
    completed: { bg: '#f9fafb', text: '#6b7280', label: 'Completed' },
  };
  const c = cfg[status] ?? cfg['proposed']!;
  return (
    <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600, background: c.bg, color: c.text }}>
      {c.label}
    </span>
  );
}

export function JobScheduler() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [date, setDate] = useState('');
  const [time, setTime] = useState('09:00');
  const [duration, setDuration] = useState(1.0);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { data: appts, isLoading } = useQuery({
    queryKey: ['appointments', jobId],
    queryFn: () => contractorApi.get<{ data: Appointment[] }>(`/contractor/jobs/${jobId}/appointments`),
    enabled: !!jobId,
  });

  const proposeMutation = useMutation({
    mutationFn: (body: { appointment_date: string; appointment_time: string; duration_hours: number; notes?: string }) =>
      contractorApi.post(`/contractor/jobs/${jobId}/appointments`, body),
    onSuccess: () => {
      setSuccess(true);
      setDate('');
      setNotes('');
      void queryClient.invalidateQueries({ queryKey: ['appointments', jobId] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to propose appointment.'),
  });

  const today = new Date().toISOString().split('T')[0]!;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (!date) { setError('Please select a date.'); return; }
    const apptBody: { appointment_date: string; appointment_time: string; duration_hours: number; notes?: string } = { appointment_date: date, appointment_time: time, duration_hours: duration };
    if (notes) apptBody.notes = notes;
    proposeMutation.mutate(apptBody);
  }

  return (
    <>
      <style>{`@media (min-width: 768px) { .js-layout { padding-left: 256px !important; } }`}</style>
      <div className="js-layout" style={{ minHeight: '100dvh', background: '#f9fafb' }}>
        {/* Header */}
        <div style={{ background: '#1D4ED8', padding: '48px 20px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 36, height: 36, borderRadius: 10, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            &#8592;
          </button>
          <h1 style={{ color: '#fff', fontSize: 20, fontWeight: 700, margin: 0 }}>Schedule Appointment</h1>
        </div>

        <div style={{ padding: '20px 16px 100px', maxWidth: 600, margin: '0 auto' }}>
          {/* Propose form */}
          <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: 20 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 16px', color: '#111827' }}>Propose a date & time</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Date</label>
                <input
                  type="date"
                  value={date}
                  min={today}
                  onChange={e => setDate(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 15, boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Time</label>
                <input
                  type="time"
                  value={time}
                  onChange={e => setTime(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 15, boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Duration: {duration === 1 ? '1 hour' : duration < 1 ? '30 min' : `${duration} hours`}
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="8"
                  step="0.5"
                  value={duration}
                  onChange={e => setDuration(parseFloat(e.target.value))}
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder='e.g. "I will call 30 min before arrival"'
                  rows={3}
                  maxLength={500}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>

              {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
              {success && <p style={{ color: '#16a34a', fontSize: 13, margin: '0 0 12px' }}>Appointment proposed! The homeowner will be notified.</p>}

              <button
                type="submit"
                disabled={proposeMutation.isPending}
                style={{
                  width: '100%', padding: '13px', borderRadius: 12, border: 'none',
                  background: proposeMutation.isPending ? '#93c5fd' : '#1D4ED8',
                  color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer',
                }}
              >
                {proposeMutation.isPending ? 'Proposing…' : 'Send appointment proposal'}
              </button>
            </form>
          </div>

          {/* Existing appointments */}
          {isLoading ? null : (appts?.data ?? []).length > 0 && (
            <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 14px', color: '#111827' }}>Appointment history</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(appts?.data ?? []).map(appt => (
                  <div key={appt.id} style={{ padding: '12px 14px', borderRadius: 10, background: '#f9fafb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#111827' }}>
                        {appt.appointment_date} at {appt.appointment_time}
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>
                        {appt.duration_hours === 1 ? '1 hour' : `${appt.duration_hours} hours`}
                        {appt.notes ? ` · ${appt.notes}` : ''}
                      </p>
                    </div>
                    {statusBadge(appt.status)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
